import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import type { ProductItem } from "@/lib/types";

export default function ProductsPage() {
  const { t } = useTranslation();
  const allProducts = t("products.items", { returnObjects: true }) as unknown as ProductItem[];
  const productCategories = t("products.categories", { returnObjects: true }) as unknown as string[];

  const [search, setSearch] = useState("");
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

  const filtered = allProducts.filter((p) => {
    const matchCat = activeCategoryIndex === 0 || p.category === productCategories[activeCategoryIndex];
    const matchSearch =
      !search ||
      p.name.includes(search) ||
      p.application.includes(search) ||
      p.material.includes(search) ||
      p.subcategory.includes(search);
    return matchCat && matchSearch;
  });

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title={t("products.page.title")}
        subtitle={t("products.page.subtitle")}
        breadcrumb={[{ label: t("products.page.title") }]}
        bgImage="https://readdy.ai/api/search-image?query=Various%20black%20industrial%20rubber%20parts%20and%20components%20arranged%20neatly%20on%20clean%20workshop%20surface%2C%20professional%20product%20photography%2C%20O-rings%20gaskets%20seals%20bushings%20of%20different%20sizes%2C%20engineering%20precision%2C%20soft%20studio%20lighting%2C%20organized%20layout&width=1600&height=680&seq=krubber-products-banner&orientation=landscape"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          {/* Category Filter */}
          <div className="flex items-center gap-2 flex-wrap mb-8 justify-center">
            {productCategories.map((cat, idx) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategoryIndex(idx)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategoryIndex === idx
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Search + Count */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
            <p className="text-sm text-foreground-600">
              {t("products.page.total", { count: filtered.length })}
              {activeCategoryIndex !== 0 && (
                <span className="text-foreground-500"> / {productCategories[activeCategoryIndex]}</span>
              )}
            </p>
            <div className="relative w-full sm:w-[300px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="text"
                placeholder={t("products.page.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <span className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-foreground-400 text-2xl" />
              </span>
              <p className="mt-4 text-foreground-600 text-sm">{t("products.page.empty")}</p>
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setActiveCategoryIndex(0);
                }}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                <i className="ri-refresh-line" />
                {t("products.page.reset")}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {filtered.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="group rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200/60 overflow-hidden transition-all cursor-pointer"
                >
                  <div className="aspect-square overflow-hidden bg-background-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                        {product.category}
                      </span>
                      <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                        {product.subcategory}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {product.name}
                    </h3>
                    <div className="mt-3 flex flex-col gap-1.5">
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-settings-3-line text-[11px]" />
                        {product.application}
                      </span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-drop-line text-[11px]" />
                        {product.material}
                      </span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-contrast-2-line text-[11px]" />
                        {product.hardness}
                      </span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary-600 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        {t("products.page.detail")}
                        <i className="ri-arrow-right-line text-[10px]" />
                      </span>
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-temp-hot-line text-[11px]" />
                        {product.tempRange}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}